import { useEffect, useState } from "react";
import { useLang } from "../contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Settings, Volume2, Play } from "lucide-react";
import {
  voicesForLang,
  isBestVoice,
  getVoiceURI,
  setVoiceURI,
  getVoiceRate,
  setVoiceRate,
  speak,
  stopSpeaking,
} from "../lib/voice";

const RATE_OPTIONS = [
  { value: 0.75, key: "voice_slow" },
  { value: 0.95, key: "voice_normal" },
  { value: 1.2, key: "voice_fast" },
];

export default function VoiceSettings() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [voices, setVoices] = useState([]);
  const [selectedURI, setSelectedURI] = useState(getVoiceURI());
  const [rate, setRate] = useState(getVoiceRate());

  useEffect(() => {
    const load = () => {
      const list = voicesForLang(lang);
      // Sort: best voices first, then alphabetical
      list.sort((a, b) => {
        const ba = isBestVoice(a) ? 0 : 1;
        const bb = isBestVoice(b) ? 0 : 1;
        if (ba !== bb) return ba - bb;
        return (a.name || "").localeCompare(b.name || "");
      });
      setVoices(list);
    };
    load();
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.onvoiceschanged = load;
    }
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
      stopSpeaking();
    };
  }, [lang, open]);

  const saveAndClose = () => {
    setVoiceURI(selectedURI);
    setVoiceRate(rate);
    stopSpeaking();
    setOpen(false);
  };

  const testVoice = () => {
    setVoiceURI(selectedURI);
    setVoiceRate(rate);
    const sample =
      lang === "fr"
        ? "Bonjour, ceci est un aperçu de la voix Sereno."
        : "Hello, this is a preview of the Sereno voice.";
    speak(sample, lang);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="voice-settings-btn"
          className="min-h-[44px] rounded-full border-2 border-[#101A42] text-base font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
        >
          <Settings className="mr-2 h-4 w-4" />
          <Volume2 className="mr-2 h-4 w-4" />
          {t("voice_settings")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-white" data-testid="voice-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#101A42]">
            {t("voice_settings")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <Label className="mb-2 block text-base font-medium">
              {t("voice_choose")}
            </Label>
            {voices.length > 0 && (
              <p
                className="mb-2 text-sm text-[#4A4A4A]"
                data-testid="voice-star-hint"
              >
                {t("voice_star_hint")}
              </p>
            )}
            {voices.length === 0 ? (
              <p
                data-testid="voice-none-available"
                className="rounded-xl border-2 border-[#A762B1]/30 bg-[#A762B1]/10 p-3 text-base text-[#6E3978]"
              >
                {t("voice_none_available")}
              </p>
            ) : (
              <Select
                value={selectedURI || "__default__"}
                onValueChange={(v) => setSelectedURI(v === "__default__" ? "" : v)}
              >
                <SelectTrigger
                  data-testid="voice-select"
                  className="h-14 rounded-xl border-2 bg-white text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="__default__" className="text-base">
                    {t("voice_default")}
                  </SelectItem>
                  {voices.map((v) => {
                    const best = isBestVoice(v);
                    return (
                      <SelectItem
                        key={v.voiceURI}
                        value={v.voiceURI}
                        className="text-base"
                        data-testid={best ? "voice-option-best" : "voice-option"}
                      >
                        {best ? "⭐ " : ""}
                        {v.name} {v.localService ? "" : "☁️"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
          </div>

          <div>
            <Label className="mb-2 block text-base font-medium">
              {t("voice_speed")}
            </Label>
            <div className="flex flex-wrap gap-2">
              {RATE_OPTIONS.map((r) => (
                <button
                  type="button"
                  key={r.value}
                  onClick={() => setRate(r.value)}
                  data-testid={`voice-rate-${r.key}`}
                  className={`min-h-[48px] rounded-full border-2 px-5 text-base font-semibold transition-colors ${
                    rate === r.value
                      ? "border-[#A762B1] bg-[#A762B1] text-white"
                      : "border-[#101A42]/25 bg-white text-[#101A42] hover:border-[#A762B1]"
                  }`}
                >
                  {t(r.key)}
                </button>
              ))}
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={testVoice}
            data-testid="voice-test-btn"
            className="min-h-[52px] w-full rounded-full border-2 border-[#101A42] text-lg font-semibold text-[#101A42] hover:bg-[#101A42] hover:text-white"
          >
            <Play className="mr-2 h-5 w-5" />
            {t("voice_test")}
          </Button>

          <Button
            type="button"
            onClick={saveAndClose}
            data-testid="voice-save-btn"
            className="min-h-[52px] w-full rounded-full bg-[#A762B1] text-lg font-semibold text-white hover:bg-[#B775C0]"
          >
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
